import * as React from 'react';
import { createRendererWithUniDriver } from '../../test/utils';
import { checkboxDriverFactory } from './Checkbox.driver';
import { Checkbox } from '.';
import eventually from 'wix-eventually';

const noop = () => {};
const defProps = {
  onChange: noop,
  label: 'label',
};

describe('Checkbox', () => {
  const createDriver = createRendererWithUniDriver(checkboxDriverFactory);
  const expectedErrorMessage = 'ErrorMessage';

  it('should render', async () => {
    const { driver } = createDriver(<Checkbox {...defProps} />);
    expect(await driver.exists()).toBe(true);
  });

  it('should call onChange with correct "checked" value', async () => {
    const onChangeSpy = jest.fn();
    const { driver } = createDriver(
      <Checkbox label="Label" onChange={onChangeSpy} />,
    );

    await driver.clickOnCheckbox();

    expect(onChangeSpy).toHaveBeenCalledWith(
      expect.objectContaining({ checked: true }),
    );
  });

  it('should show error state - old error', async () => {
    const { driver } = createDriver(<Checkbox error {...defProps} />);

    expect(await driver.hasError()).toBeTruthy();
  });

  it('should show error state - new error', async () => {
    const { driver } = createDriver(
      <Checkbox error newErrorMessage {...defProps} />,
    );
    expect(await driver.hasError()).toBeTruthy();
  });

  it('should show error message tooltip', async () => {
    const { driver } = createDriver(
      <Checkbox error errorMessage={expectedErrorMessage} {...defProps} />,
    );
    const errorIcon = driver.errorIcon;
    const iconButtonDescription = errorIcon.getIconButtonDescription();
    const tooltipContent = errorIcon.getTooltipContent();
    expect(await iconButtonDescription.text()).toBe(expectedErrorMessage);
    expect(await tooltipContent.exists()).toBe(false);

    await errorIcon.click();

    expect(await iconButtonDescription.exists()).toBe(false);
    expect(await tooltipContent.text()).toBe(expectedErrorMessage);
    expect(await driver.getErrorMessage()).toBe(expectedErrorMessage);
  });

  it('should show new error message', async () => {
    const { driver } = createDriver(
      <Checkbox
        error
        newErrorMessage
        errorMessage={expectedErrorMessage}
        {...defProps}
      />,
    );

    expect(await driver.getErrorMessage()).toBe(expectedErrorMessage);
  });

  it('should show disabled state', async () => {
    const { driver } = createDriver(<Checkbox disabled {...defProps} />);

    expect(await driver.hasDisabled()).toBeTruthy();
  });

  it('should show indeterminate state', async () => {
    const { driver } = createDriver(<Checkbox indeterminate {...defProps} />);

    expect(await driver.hasIndeterminate()).toBeTruthy();
  });

  it('should show checked state', async () => {
    const { driver } = createDriver(<Checkbox checked {...defProps} />);

    expect(await driver.hasChecked()).toBeTruthy();
  });

  it('should toggle error tooltip when clicking on error icon', async () => {
    const { driver } = createDriver(
      <Checkbox error errorMessage={expectedErrorMessage} {...defProps} />,
    );

    expect(await driver.isErrorTooltipShown()).toBeFalsy();

    await driver.clickOnErrorIcon();

    await eventually(async () => {
      expect(await driver.isErrorTooltipShown()).toBeTruthy();
    });

    await driver.clickOnErrorIcon();

    await eventually(async () => {
      expect(await driver.isErrorTooltipShown()).toBeFalsy();
    });
  });

  it('should allow focusing inner input', async () => {
    const ref = React.createRef<Checkbox>();
    const { driver } = createDriver(<Checkbox ref={ref} {...defProps} />);

    expect(await driver.isFocused()).toBeFalsy();
    ref.current.focus();
    expect(await driver.isFocused()).toBeTruthy();
  });

  it('should set the correct a11y attributes', async () => {
    const { driver } = createDriver(
      <Checkbox error errorMessage={expectedErrorMessage} {...defProps} />,
    );

    await driver.clickOnErrorIcon();

    await eventually(async () => {
      expect(await driver.isErrorTooltipShown()).toBeTruthy();
    });

    const ariaDescribedBy = await driver.getCheckboxAriaDescribedby();

    expect(ariaDescribedBy).toBeDefined();

    const errorMessage = document.querySelector(`#${ariaDescribedBy}`);

    expect(errorMessage).not.toBeNull();
    expect(errorMessage.textContent).toBe(expectedErrorMessage);
  });

  it('aria describedby should be concatenated to value from props', async () => {
    const { driver } = createDriver(
      <Checkbox
        error
        errorMessage={expectedErrorMessage}
        aria-describedby="some description"
        {...defProps}
      />,
    );

    await driver.clickOnErrorIcon();

    await eventually(async () => {
      expect(await driver.isErrorTooltipShown()).toBeTruthy();
    });

    await driver.clickOnErrorIcon();

    expect(await driver.getCheckboxAriaDescribedby()).toContain(
      'some description',
    );
  });

  it('should set aria-label to checkbox input', async () => {
    const ariaLabel = 'test checkbox label';
    const { driver } = createDriver(
      <Checkbox aria-label={ariaLabel} {...defProps} />,
    );

    expect(await driver.getCheckboxAriaLabel()).toBe(ariaLabel);
  });

  it('should set lang to root', async () => {
    const lang = 'he';
    const { driver } = createDriver(<Checkbox lang={lang} {...defProps} />);

    expect(await driver.getCheckboxLang()).toBe(lang);
  });
  it('should pass required prop', async () => {
    const { driver } = createDriver(<Checkbox required {...defProps} />);
    expect(await driver.isRequired()).toBeTruthy();
  });
  it('should show asterisk, when checkbox is required ', async () => {
    const { driver } = createDriver(<Checkbox required {...defProps} />);
    expect(await driver.getText()).toBe(`${defProps.label}*`);
  });
  it('should not show asterisk, when checkbox is required but showAsterisk is false', async () => {
    const { driver } = createDriver(
      <Checkbox required showAsterisk={false} {...defProps} />,
    );
    expect(await driver.getText()).toBe(defProps.label);
  });
});
